import {
  Dialog,
  Typography,
  DialogTitle,
  DialogContent,
  Box,
  makeStyles,
  IconButton,
  DialogActions,
  CircularProgress,
} from '@material-ui/core';
import React, { useState, useCallback, useEffect, ReactElement, ChangeEvent } from 'react';
import { Scenario } from 'scenario/ScenarioType';
import CloseIcon from '@material-ui/icons/Close';
import { colors, Button, OutlinedInputWithLabel } from '@novozymes/components';
import API from 'API';
import * as EmailValidator from 'email-validator';
import { useTranslation } from 'react-i18next';
// import logger from 'utils/logger';
import { getUserEmail } from 'utils/getUser';

export interface ShareScenarioDialogProps {
  show: boolean;
  scenario?: Scenario;
  onClose(): void;
}

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: colors.black67,
  },
}));

const dialogStyles = makeStyles(() => ({
  container: {
    backgroundColor: colors.black67,
  },
}));

const ShareScenarioDialog = ({ show, scenario, onClose }: ShareScenarioDialogProps): ReactElement => {
  const [open, setOpen] = useState(show);
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation('shareScenarioDialog');

  const classes = useStyles();
  const dialogClasses = dialogStyles();

  const handleClose = useCallback(() => {
    setOpen(false);
    setEmail('');
    setError('');
    setSuccess(false);
    onClose();
  }, []);

  const handleShare = useCallback(async () => {
    setError('');
    if (!EmailValidator.validate(email)) {
      setError(t('invalidEmail'));
      return;
    }

    const userEmail = await getUserEmail();

    if (userEmail === email) {
      setError(t('shareAnotherUser'));
      return;
    }

    if (!scenario?.id) {
      setError(t('saveScenario'));
      return;
    }

    setLoading(true);

    const body = {
      shareWithEmail: email,
    };

    try {
      const response = await API.Scenarios.share({ id: scenario.id, body });
      const responseData = response.data<{ statusCode: string; body: any; headers: any }>();

      if (responseData.statusCode === '201') {
        setError('');
        setSuccess(true);
      } else {
        setError(t('ensureSfAccount'));
      }
    } catch (e) {
      // logger.error('Failed to share scenario', { error: e });
      setError(t('error'));
    } finally {
      setLoading(false);
    }
    setLoading(false);
  }, [email, scenario]);

  useEffect(() => {
    if (scenario !== undefined) setOpen(show);
  }, [show, scenario]);

  return (
    <Dialog classes={dialogClasses} onClose={handleClose} open={open}>
      <DialogTitle disableTypography={false}>
        <Box mt={2} mx={3}>
          <Typography variant="body1">{t('title')}</Typography>
        </Box>

        <IconButton
          id="btn-share-dialog-close"
          aria-label="close"
          className={classes.closeButton}
          onClick={handleClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box mx={2}>
          {success ? (
            <>
              <Typography id="successful-share-info" variant="body1">
                {t('successMessage', { email })}
              </Typography>
            </>
          ) : (
            <>
              <Box mb={2}>
                <Typography variant="body1">{t('description')}</Typography>
              </Box>
              <OutlinedInputWithLabel
                label={t('emailInputLabel')}
                value={email}
                placeholder="email"
                handleChange={(event: ChangeEvent<HTMLInputElement>): void => {
                  const { value: newValue } = event.target;
                  setEmail(newValue);
                }}
                error={!!error}
                errorMessage={error}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Box mb={2} mt={4} mr={2}>
          {success ? (
            <>
              <Button id="btn-share-dialog-success-close" type="primary" onClick={handleClose}>
                {t('close')}
              </Button>
            </>
          ) : (
            <>
              <Button id="btn-share-dialog-cancel" type="secondary" onClick={handleClose}>
                {t('cancel')}
              </Button>
              {loading ? (
                <CircularProgress color="primary" />
              ) : (
                <Button id="btn-share-dialog-share" type="primary" onClick={handleShare}>
                  {t('shareButton')}
                </Button>
              )}
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ShareScenarioDialog;
